/* eslint-disable no-console */

import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

const API_CLIENT: AxiosInstance = axios.create({
  baseURL: '/api',
  withCredentials: true,
});

export default {
  async execute(
    method: 'get' | 'GET' | 'delete' | 'DELETE' | 'head' | 'HEAD' | 'options' | 'OPTIONS' | 'post' | 'POST' | 'put' | 'PUT' | 'patch' | 'PATCH' | 'purge' | 'PURGE' | 'link' | 'LINK' | 'unlink' | 'UNLINK' | undefined,
    resource: string | undefined,
    data?: any,
    params?: any,
  ) {
    const options: AxiosRequestConfig = {
      method,
      url: resource,
      data,
      params,
    };

    return API_CLIENT(options)
      .then((req) => req.data)
      .catch((error) => {
        console.log('API_CLIENT -> catch error');
        return error;
      });
  },

  async executeExport(
    resource: string,
    params: any = null,
    filename = 'Syndesia - Esportazione Commesse.xlsx',
  ) {
    const options: AxiosRequestConfig = {
      method: 'get',
      url: resource,
      params,
      responseType: 'text',
      headers: { 'Content-Type': 'application/octet-stream' },
    };

    return API_CLIENT(options)
      .then((response) => {
        const byteCharacters = atob(response.data);
        const byteNumbers = new Array(byteCharacters.length);
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        if (typeof (window.navigator as any).msSaveBlob !== 'undefined') {
          (window.navigator as any).msSaveBlob(blob, filename);
        } else {
          const blobURL = window.URL.createObjectURL(blob);
          const tempLink = document.createElement('a');
          tempLink.style.display = 'none';
          tempLink.href = blobURL;
          tempLink.setAttribute('download', filename);

          if (typeof tempLink.download === 'undefined') {
            tempLink.setAttribute('target', '_blank');
          }
          document.body.appendChild(tempLink);
          tempLink.click();
          document.body.removeChild(tempLink);
          window.URL.revokeObjectURL(blobURL);
        }
        return Promise.resolve();
      })
      .catch((error) => {
        console.log('API_CLIENT -> catch error');
        return error;
      });
  },

  login(data: any) {
    return this.execute('post', '/login', data);
  },

  logout() {
    return this.execute('get', '/logout');
  },

  fetchUser() {
    return this.execute('get', '/me');
  },

  // Recupera la singola Lead utilizzando la v2 delle API
  getLead(id: number) {
    return this.execute('get', `/v2/lead/${id}`);
  },

  // Recupera i dati supply di una lead (padre o figlia)
  getLeadSupply(leadId: number) {
    return this.execute('get', `/lead/${leadId}/supply`);
  },

  getCategories(params?: any) {
    return this.execute('get', '/categories', null, params);
  },

  getTags(params?: any) {
    return this.execute('get', '/tags', null, params);
  },

  getNetworks() {
    return this.execute('get', '/networks');
  },

  updateLeadContact(id: number, data: any) {
    return this.execute('put', `/lead/${id}/contact`, data);
  },

  updateLeadDetails(id: number, data: any) {
    return this.execute('put', `/lead/${id}/details`, data);
  },

  updateLeadPayments(id: number, data: any) {
    return this.execute('put', `/lead/${id}/payments`, data);
  },

  updateLeadSupply(id: number, data: any) {
    return this.execute('put', `/lead/${id}/supply`, data);
  },

  updateLeadOwners(id: number, data: any) {
    return this.execute('put', `/lead/${id}/owners`, data);
  },

  // Recupera l'elenco utenti utilizzando la v2 delle API
  getUsers(params?: any) {
    return this.execute('get', '/v2/users', null, params);
  },

  getUser(id: number) {
    return this.execute('get', `/user/${id}`);
  },

  getUserName(id: number) {
    return this.execute('get', `/user/${id}/name`);
  },

  updateLeadPrivateNotes(id: number, data: string) {
    return this.execute('put', `/lead/${id}/privateNotes`, data);
  },

  updateLeadSharedNotes(id: number, data: string) {
    return this.execute('put', `/lead/${id}/sharedNotes`, data);
  },

  updateLeadOpNotes(id: number, data: string) {
    return this.execute('put', `/lead/${id}/opNotes`, data);
  },

  updateLeadPracticeNotes(id: number, data: string) {
    return this.execute('put', `/lead/${id}/practiceNotes`, data);
  },

  updateLeadAssignmentNotes(leadId: number, data: { customValueName: string; notes: string }) {
    return this.execute('put', `/lead/${leadId}/assignmentNotes`, data);
  },

  resetEcobonusCheck(id: number) {
    return this.execute('put', `/lead/${id}/ecReset`);
  },

  updateLeadTask(leadId: number, taskId: number, data: any) {
    return this.execute('put', `/lead/${leadId}/task/${taskId}/update`, data);
  },

  updateLeadAssignments(leadId: number, data: any) {
    return this.execute('put', `/lead/${leadId}/assignments`, data);
  },

  removeLeadAttachments(id: number, data: any) {
    return this.execute('delete', `/lead/${id}/attachments`, data);
  },

  updateLeadAttachments(id: number, data: any) {
    return this.execute('put', `/lead/${id}/attachments`, data);
  },

  updateAttachmentType(attachment: number, type: string) {
    return this.execute('put', `/attachment/${attachment}/type/${type}`);
  },

  getAttachmentsDetails() {
    return this.execute('get', '/attachments/details');
  },

  // Elimina gli allegati specificati
  // attachments: array di ID degli allegati da eliminare
  deleteAttachments(attachments: number[]) {
    return this.execute('delete', '/attachments', { attachments });
  },

  assignChildLead(id: number, data: any) {
    return this.execute('put', `/lead/${id}/child/assign`, data);
  },

  removeChildLead(id: number, data: any) {
    return this.execute('put', `/lead/${id}/child/remove`, data);
  },

  addChildLead(id: number, data: any) {
    return this.execute('put', `/lead/${id}/child/add`, data);
  },

  createChildLead(id: number, data: any) {
    return this.execute('put', `/lead/${id}/child/create`, data);
  },

  removeLeadChecklist(leadId: number, checklistId: number) {
    return this.execute('put', `/lead/${leadId}/removeChecklist`, { checklistId });
  },

  leadGhostTasksCleanup(id: number) {
    return this.execute('put', `/lead/${id}/ghostTasksCleanup`) as Promise<{ removedCount: number; message: string }>;
  },

  renameChildLead(id: number, data: any) {
    return this.execute('put', `/lead/${id}/child/rename`, data);
  },

  convertLead(id: number, data: any) {
    return this.execute('put', `/lead/${id}/convert`, data);
  },

  reopenLead(id: number) {
    return this.execute('put', `/lead/${id}/reopen`);
  },

  eraseLead(id: number, eraseChildren = true) {
    return this.execute('delete', `/lead/${id}`, undefined, { eraseChildren });
  },

  closeLead(id: number, data: any) {
    return this.execute('put', `/lead/${id}/close`, data);
  },

  resolveLead(id: number) {
    return this.execute('put', `/lead/${id}/resolve`);
  },

  cancelLead(id: number) {
    return this.execute('put', `/lead/${id}/cancel`);
  },

  rejectLead(id: number, data: any) {
    return this.execute('put', `/lead/${id}/reject`, data);
  },

  takeLead(id: number) {
    return this.execute('put', `/lead/${id}/take`);
  },

  reassignLead(id: number, data: any) {
    return this.execute('put', `/lead/${id}/reassign`, data);
  },

  getLeads(params?: any) {
    return this.execute('get', '/leads', null, params);
  },

  // Recupera le Lead dell'elenco utilizzando la v2 delle API
  getFullLeads(params?: any) {
    return this.execute('get', '/v2/full-leads', null, params);
  },

  getSharedNotes(id: number) {
    return this.execute('get', `/lead/${id}/sharedNotes`);
  },

  getPrivateNotes(id: number) {
    return this.execute('get', `/lead/${id}/privateNotes`);
  },

  relinkLead(id: number, data: any) {
    return this.execute('put', `/lead/${id}/relink`, data);
  },

  // updateUserSettings(data: any) {
  //   return this.execute('put', '/me/settings', data);
  // },

  convertTask(leadId: number, taskId: number | null, data: any) {
    return this.execute('put', `/lead/${leadId}/task/${taskId}/convert`, data);
  },

  getOperations(params?: any) {
    return this.execute('get', '/operations', null, params);
  },

  getOperationsCount(params?: any) {
    return this.execute('get', '/operations/count', null, params);
  },

  exportOperations(params: any) {
    return this.executeExport('/operations/export', params);
  },

  exportArchivables() {
    return this.executeExport('/operations/exportArchivables', null, 'Syndesia - Commesse Archiviabili.xlsx');
  },

  exportTimestamps() {
    return this.executeExport('/operations/exportLockInfo', null, 'Informazioni locking.xlsx');
  },

  getOpNotes(id: number) {
    return this.execute('get', `/operation/${id}/opNotes`);
  },

  getTargetDateNotes(id: number) {
    return this.execute('get', `/operation/${id}/targetDateNotes`);
  },

  getSupplyNotes(id: number) {
    return this.execute('get', `/operation/${id}/supplyNotes`);
  },

  getPaymentsNotes(id: number) {
    return this.execute('get', `/operation/${id}/paymentsNotes`);
  },

  getContactDetails(id: number) {
    return this.execute('get', `/operation/${id}/contactDetails`);
  },

  getOperationDates(id: number) {
    return this.execute('get', `/operation/${id}/dates`);
  },

  getOperation(id: number) {
    return this.execute('get', `/operation/${id}`);
  },

  updateOperationsFilters(data: unknown) {
    return this.execute('put', '/me/operationsFilters', data);
  },

  // Connessioni (connections)

  getConnection(id: number) {
    return this.execute('get', `/connection/${id}`);
  },

  getConnections(params?: unknown) {
    return this.execute('get', '/connections', null, params);
  },

  getConnectionsCount(params?: unknown) {
    return this.execute('get', '/connections/count', null, params);
  },

  exportConnections(params: any) {
    return this.executeExport('/connections/export', params, 'Syndesia - Esportazione Connessioni.xlsx');
  },

  updateConnectionsFilters(data: unknown) {
    return this.execute('put', '/me/connectionsFilters', data);
  },

  updateLastView(data: unknown) {
    return this.execute('put', '/me/lastView', data);
  },

  // Operations Filter Presets
  getOperationsFilterPresets() {
    return this.execute('get', '/me/operationsFilterPresets');
  },

  updateOperationsFilterPresets(data: unknown) {
    return this.execute('put', '/me/operationsFilterPresets', data);
  },

  // Connections Filter Presets
  getConnectionsFilterPresets() {
    return this.execute('get', '/me/connectionsFilterPresets');
  },

  updateConnectionsFilterPresets(data: unknown) {
    return this.execute('put', '/me/connectionsFilterPresets', data);
  },

  // Richiede che i campi della commessa specificati vengano aggiornati
  updateConnection(id: number, data: { [key: string]: never }) {
    return this.execute('put', `/connection/${id}`, data);
  },

  getConnectionProgressDetails(id: number, type: string) {
    return this.execute('get', `/connection/${id}/progress/${type}/details`);
  },

  cancelConnection(id: number, data: any) {
    return this.execute('put', `/connection/${id}/cancel`, data);
  },

  getCancelledConnections(id: number) {
    return this.execute('get', `/connection/${id}/cancelled`);
  },

  checkDateAvailability(id: number, dateString: string) {
    return this.execute('get', `/operation/${id}/checkDateAvailability/${dateString}`);
  },

  checkBusyDatesForDate(id: number, eventType: string, dateString: string) {
    return this.execute('get', `/operation/${id}/checkBusyDatesForDate/${eventType}/${dateString}`);
  },

  createOperationZip(id: number) {
    return this.execute('put', `/operation/${id}/zip/create`);
  },

  downloadOperationZip(id: number) {
    // Apre direttamente il link per il download del file ZIP
    return window.open(`/api/operation/${id}/zip/download`, '_self');
  },

  uploadOperationZip(id: number) {
    return this.execute('put', `/operation/${id}/zip/backup`);
  },

  verifyOperationZip(id: number) {
    return this.execute('put', `/operation/${id}/zip/verify`);
  },

  archiveOperation(id: number) {
    return this.execute('put', `/operation/${id}/archive`);
  },

  removeOperationZip(id: number) {
    return this.execute('delete', `/operation/${id}/zip`);
  },

  checkOperationStatus(id: number) {
    return this.execute('get', `/operation/${id}/status`);
  },

  // Aggiorna la data/ora di ultima attività dell'utente corrente
  updateLastActivity() {
    return this.execute('put', '/me/activity');
  },

  // Configurazione controllo duplicati
  getDuplicateCheckExcludedCategories() {
    return this.execute('get', '/config/duplicate-check-excluded-categories');
  },

  updateDuplicateCheckExcludedCategories(categoryIds: number[]) {
    return this.execute('put', '/config/duplicate-check-excluded-categories', { categoryIds });
  },

  getAssignmentTypes() {
    return this.execute('get', '/config/assignment-types');
  },

  getIntegrations() {
    return this.execute('get', '/config/integrations');
  },

  // Groups, Templates, Checklists (legacy v1 endpoints)
  getGroups() {
    return this.execute('get', '/v1/groups');
  },

  getTemplates() {
    return this.execute('get', '/v1/templates');
  },

  getChecklists() {
    return this.execute('get', '/v1/checklists');
  },

  // Regole
  getRules() {
    return this.execute('get', '/v1/rules');
  },

  getRule(id: number) {
    return this.execute('get', `/v1/rule/${id}`);
  },

  createRule(data: any) {
    return this.execute('post', '/v1/rule', data);
  },

  updateRule(id: number, data: any) {
    return this.execute('put', `/v1/rule/${id}`, data);
  },

  toggleRule(id: number) {
    return this.execute('put', `/v1/rule/${id}/toggle`);
  },

  deleteRule(id: number) {
    return this.execute('delete', `/v1/rule/${id}`);
  },

  getRuleEvents() {
    return this.execute('get', '/v1/events');
  },

  getRuleConditions() {
    return this.execute('get', '/v1/conditions');
  },

  getRuleActions() {
    return this.execute('get', '/v1/actions');
  },

  // Archivio delle regole del vecchio portale
  getRulesArchive() {
    return this.execute('get', '/v1/rules/archive');
  },

  getRulesArchiveEntry(id: number) {
    return this.execute('get', `/v1/rules/archive/${id}`);
  },

  // Test regole
  testRules(leadId: number, simulatedDate?: string) {
    return this.execute('post', '/v1/rules/test', {
      leadId,
      simulatedDate: simulatedDate || new Date().toISOString().split('T')[0], // YYYY-MM-DD
    });
  },

  // Log delle esecuzioni delle regole
  getRuleExecutions(params: Record<string, string | number | undefined>) {
    return this.execute('get', '/v1/rules/executions', undefined, params);
  },

  getRuleExecution(id: number) {
    return this.execute('get', `/v1/rules/executions/${id}`);
  },

  getRuleRuns(params: Record<string, string | number | undefined>) {
    return this.execute('get', '/v1/rules/runs', undefined, params);
  },
};
